import { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { H2 } from '../../../../common/Headings/Headings.styles';
import { TableRowWrapper } from '../../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Button from '../../../../components/Button/Button';
import Table from '../../../../components/Table/Table';
import { useGetSpecs } from '../../../../services/spec/spec.service';
import { Spec } from '../../../../services/spec/spec.types';
import { ButtonContainer, SelectOptionRecipesContainer, StyledTableWrapper } from './SelectOptionRecipes.styles';
import { SelectOptionRecipesProps } from './SelectOptionRecipes.types';

const SelectOptionRecipes: FC<SelectOptionRecipesProps> = ({
  selectedOptionRecipes,
  setSelectedOptionRecipes,
  setStep,
}) => {
  const availableGridRef = useRef<AgGridReact>(null);
  const selectedGridRef = useRef<AgGridReact>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchErrorMessage, setSearchErrorMessage] = useState('');

  const { data: specsData } = useGetSpecs({
    isSubRecipe: true
  });

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
    if (searchErrorMessage) setSearchErrorMessage('');
  };

  const handleAddSpecClick = () => {
    const matchingSpec = specsData?.data?.find((spec) => spec.specNo === +searchInputValue);
    if (matchingSpec) {
      availableGridRef?.current?.api?.forEachNode((node) => {
        if (node.data.specNo.toString() === searchInputValue) {
          node.setSelected(true);
        }
      });
      setSearchInputValue('');
    } else {
      setSearchErrorMessage('Spec not found');
    }
  };

  const handleClear = () => {
    setSearchInputValue('');
    setSearchErrorMessage('');
  };

  const selectAvailableSubrecipeSpecs = (selectedRows: Spec[]) => {
    availableGridRef?.current?.api?.forEachNode((node) => {
      const matchingItem = selectedRows.find(
        (selectedOptionRecipe) => selectedOptionRecipe.specNo === node?.data?.specNo
      );
      if (matchingItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  };

  useEffect(() => {
    selectedGridRef?.current?.api?.selectAll();
    selectAvailableSubrecipeSpecs(selectedOptionRecipes);
  }, [selectedOptionRecipes, selectedGridRef?.current?.api]);

  const onSelectionChangedAvailable = (event: SelectionChangedEvent) => {
    setSelectedOptionRecipes(event.api.getSelectedRows());
  };

  const onSelectionChangedSelected = (event: SelectionChangedEvent) => {
    selectAvailableSubrecipeSpecs(event.api.getSelectedRows());
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAddSpecClick();
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <SelectOptionRecipesContainer>
      <H2>Select option recipes to copy</H2>
      <TableRowWrapper>
        <StyledTableWrapper>
          <Table
            ref={availableGridRef}
            showHeader={true}
            headerTitle='Available Specs'
            columnDefs={[
              {
                minWidth: 70,
                maxWidth: 70,
                flex: 1,
                headerName: 'Select',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 120, flex: 1, field: 'specNo', headerName: 'Spec' },
              { minWidth: 220, flex: 2, field: 'description', headerName: 'Description', resizable: false },
            ]}
            rowData={specsData?.data}
            rowSelection='multiple'
            suppressRowClickSelection={true}
            onSelectionChanged={onSelectionChangedAvailable}
            returnedRecords={specsData?.data?.length || 0}
            height={'100%'}
          />
        </StyledTableWrapper>
        <StyledTableWrapper>
          <Table
            ref={selectedGridRef}
            showHeader={true}
            headerButtons={[
              {
                inputProps: {
                  label: '',
                  id: 'addSpec',
                  value: searchInputValue,
                  onChange: handleSearchInputChange,
                  errorText: searchErrorMessage ? 'Spec not found' : '',
                  'aria-label': 'add spec',
                  ref: inputRef,
                  onKeyDown: handleKeyDown,
                },
                buttonProps: {
                  label: searchErrorMessage ? 'Clear' : 'Add Spec',
                  onClick: searchErrorMessage ? handleClear : handleAddSpecClick,
                },
              },
            ]}
            headerTitle='Selected Specs'
            columnDefs={[
              {
                minWidth: 70,
                maxWidth: 70,
                field: '',
                headerName: 'Select',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
                flex: 1,
              },
              { minWidth: 120, maxWidth: 220, field: 'specNo', headerName: 'Spec', flex: 1 },
              { minWidth: 320, field: 'description', headerName: 'Description', flex: 1, resizable: false },
            ]}
            rowData={selectedOptionRecipes}
            rowSelection='multiple'
            suppressRowClickSelection={true}
            onSelectionChanged={onSelectionChangedSelected}
            returnedRecords={selectedOptionRecipes.length}
            height={'100%'}
          />
        </StyledTableWrapper>
      </TableRowWrapper>
      <ButtonContainer>
        <Button label='Next' onClick={() => setStep(1)} disabled={selectedOptionRecipes.length === 0} />
      </ButtonContainer>
    </SelectOptionRecipesContainer>
  );
};

export default SelectOptionRecipes;
